import { websocketUrl } from "../../utils/constants";
import { ensureSubscription, dropSubscription } from "./cableClient";
import { getTabId } from "../../utils/tabId";
import { getWatchlistData } from "../../api/user";
import { mergeQuoteIntoSeriesMap } from "./liveChartQuotes";

const STREAM_RENDER_STEP_MS = 34;
const STREAM_RENDER_MIN_CHARS = 3;
const STREAM_RENDER_MAX_CHARS = 22;

const buildBotMessage = (message) => ({
  __stableKey: `stk-${Math.random().toString(36).slice(2, 10)}`,
  createdAt: "2023-09-29T22:28:02Z",
  message,
  advancedFilteringMessage: "",
  sender: "bot",
  tickerDescription: [],
  answeredTickerIds: [],
  currentPrice: "",
  closePrice: "",
  pricePctChange1d: "",
  pricePctChangeAh: "",
  pricePctChange24hr: "",
  assetType: "",
  ticker: "",
  currentPriceUpdatedAt: "",
  logoUrls: [],
  tickerPrices: null,
  earningsInfo: null,
  revenueInfo: null,
});

const appendBotMessageText = (setMessageThreads, text) => {
  if (!text) return;
  setMessageThreads((prev) => {
    if (prev.length && prev[prev.length - 1].sender === "bot") {
      const next = prev.slice();
      next[next.length - 1] = {
        ...next[next.length - 1],
        message: `${next[next.length - 1].message || ""}${text}`,
      };
      return next;
    }

    return [...prev, buildBotMessage(text)];
  });
};

const replaceBotMessageText = (setMessageThreads, text) => {
  setMessageThreads((prev) => {
    if (prev.length && prev[prev.length - 1].sender === "bot") {
      const next = prev.slice();
      next[next.length - 1] = {
        ...next[next.length - 1],
        message: text,
      };
      return next;
    }

    return [...prev, buildBotMessage(text)];
  });
};

const getRenderChunkSize = (pendingLength) => {
  if (pendingLength >= 220) return STREAM_RENDER_MAX_CHARS;
  if (pendingLength >= 140) return 18;
  if (pendingLength >= 80) return 12;
  if (pendingLength >= 36) return 8;
  return STREAM_RENDER_MIN_CHARS;
};

const takeRenderChunk = (pendingText) => {
  if (!pendingText) return ["", ""];

  const target = Math.min(
    pendingText.length,
    getRenderChunkSize(pendingText.length),
  );

  if (pendingText.length <= target) {
    return [pendingText, ""];
  }

  const preferred = pendingText
    .slice(0, target + 12)
    .match(/^([\s\S]{1,34}?[\s,.!?;:)\]}]+)(?=[^\s]|$)/);

  if (preferred?.[1]?.length && preferred[1].length >= target) {
    const chunk = preferred[1];
    return [chunk, pendingText.slice(chunk.length)];
  }

  const chunk = pendingText.slice(0, target);
  return [chunk, pendingText.slice(target)];
};

// Define a function that establishes a WebSocket connection
export const connectWebSocket = (
  setQuestionOnDemand,
  current_session_id,
  websocketSubscriptionRef,
  setMessageThreads,
  showLoadingWaitingMessage,
  setShowLoadingWaitingMessage,
  useFullWidth,
  setUseFullWidth,
  setLoadingPhaseMessage,
  setLoadingPhaseDetail,
  // setDataProcessesMessage,
  pushDataProcess,
  pushAnalysisTraceEvent,
  setLiveTextOutputting,
  setLatestTickerPrices,
  setDisplayUpdatedInfo,
  setWatchlistData,
  setAnimatedItems,
  activeThread = null,
  websocketActiveChartRef,
  options = {},
) => {
  const { setAsPrimary = true } = options || {};
  const threadUuid = activeThread?.threadUuid ?? null;
  const tabId = getTabId();
  const identifier = {
    channel: "WebsocketMessageChannel",
    thread_uuid: threadUuid,
    tab_id: tabId,
  };
  const key = JSON.stringify(identifier);  // unique per-tab+thread
  const streamRenderState = {
    activeStreamMessageId: null,
    pendingText: "",
    completionPending: false,
    drainTimer: null,
    destroyed: false,
    renderEpoch: 0,
  };

  const clearDrainTimer = () => {
    if (!streamRenderState.drainTimer) return;
    clearTimeout(streamRenderState.drainTimer);
    streamRenderState.drainTimer = null;
  };

  const finishStreamIfIdle = () => {
    if (!streamRenderState.completionPending || streamRenderState.pendingText) {
      return;
    }
    streamRenderState.completionPending = false;
    streamRenderState.activeStreamMessageId = null;
    setLiveTextOutputting(false);
  };

  const drainBufferedText = (scheduledEpoch = streamRenderState.renderEpoch) => {
    streamRenderState.drainTimer = null;
    if (streamRenderState.destroyed) return;
    if (scheduledEpoch !== streamRenderState.renderEpoch) return;

    const [chunk, remaining] = takeRenderChunk(streamRenderState.pendingText);
    if (!chunk) {
      finishStreamIfIdle();
      return;
    }

    streamRenderState.pendingText = remaining;
    appendBotMessageText(setMessageThreads, chunk);
    if (showLoadingWaitingMessage != null) setShowLoadingWaitingMessage(null);

    if (streamRenderState.pendingText) {
      const nextEpoch = streamRenderState.renderEpoch;
      streamRenderState.drainTimer = setTimeout(
        () => drainBufferedText(nextEpoch),
        STREAM_RENDER_STEP_MS,
      );
      return;
    }

    finishStreamIfIdle();
  };

  const scheduleDrain = () => {
    if (
      streamRenderState.destroyed ||
      streamRenderState.drainTimer ||
      !streamRenderState.pendingText
    ) {
      return;
    }

    const scheduledEpoch = streamRenderState.renderEpoch;
    streamRenderState.drainTimer = setTimeout(
      () => drainBufferedText(scheduledEpoch),
      STREAM_RENDER_STEP_MS,
    );
  };

  const enqueueStreamText = (text, streamMessageId = null) => {
    const nextText = String(text || "");
    if (!nextText) return;

    if (
      streamMessageId &&
      streamRenderState.activeStreamMessageId &&
      streamRenderState.activeStreamMessageId !== streamMessageId
    ) {
      clearDrainTimer();
      streamRenderState.pendingText = "";
      streamRenderState.completionPending = false;
      streamRenderState.renderEpoch += 1;
    }

    if (streamMessageId) {
      streamRenderState.activeStreamMessageId = streamMessageId;
    }

    setLiveTextOutputting(true);
    streamRenderState.pendingText += nextText;
    scheduleDrain();
  };

  const resetBufferedStream = ({
    text = "",
    completed = false,
    streamMessageId = null,
  } = {}) => {
    clearDrainTimer();
    streamRenderState.renderEpoch += 1;
    streamRenderState.pendingText = "";
    streamRenderState.completionPending = false;
    streamRenderState.activeStreamMessageId = streamMessageId || null;

    if (typeof text === "string" && text.length > 0) {
      replaceBotMessageText(setMessageThreads, text);
    }

    setLiveTextOutputting(!completed);
  };

  // Ensure one subscription per key and one consumer per tab
  const subscription = ensureSubscription(
    key,
    { url: websocketUrl, identifier },
    {
      connected() {
        this._aliveTimer && clearInterval(this._aliveTimer);
        this.perform("alive");                      // kick it once
        this._aliveTimer = setInterval(() => {
          try { this.perform("alive"); } catch (_) {}
        }, 5000);
      },
      disconnected() {
        if (this._aliveTimer) clearInterval(this._aliveTimer);
      },
      rejected() {
        if (this._aliveTimer) clearInterval(this._aliveTimer);
      },
      received(data) {
        if (data["audio_chunk_b64"]) {
          if (typeof window !== "undefined") {
            try {
              window.dispatchEvent(
                new CustomEvent("incite:audio-chunk", {
                  detail: {
                    threadUuid,
                    audioChunkB64: data["audio_chunk_b64"],
                    audioFormat: data["audio_format"] || "mp3",
                    audioSeq: data["audio_seq"] ?? null,
                  },
                }),
              );
            } catch {}
          }
          return;
        }

        // This is JUST for the loading phases that appear below the chat
        if (data["loading_phase"]) {
          setLoadingPhaseMessage(data["loading_phase"]);
          return;
        }

        if (Object.prototype.hasOwnProperty.call(data, "loading_phase_detail")) {
          setLoadingPhaseDetail(data["loading_phase_detail"]);
          return;
        }

        if (data["analysis_trace_event"]) {
          pushAnalysisTraceEvent?.(data["analysis_trace_event"]);
          return;
        }

        // If there was an agentic watchlist reload
        if (data["watchlist_reload"]) {
          getWatchlistData(setWatchlistData);
          return;
        }

        // This is for the data process that will appear in the side bar.
        if (data["data_processes"]) {
          // setDataProcessesMessage(data["data_processes"]);
          // This had to become a queue because how how many are set at once!! otherwise replaces and not all will show on side data processes panel
          pushDataProcess({
            message: data["data_processes"],
            require_completion: data["require_completion"],
          });
          return;
        }

        // used to mark compelete (if state requires it)
        if (data["data_processes_complete"]) {
          pushDataProcess({
            message: data["data_processes_complete"],
            complete: true,
          });
          return;
        }

        if (data["on-demand"]) {
          setTimeout(() => setQuestionOnDemand(true), 500);
          return;
        }
        if (data["overwrite-question"]) {
          // IMPORTANT: never mutate state in place
          setMessageThreads(prev => {
            if (!prev.length) return prev;
            const next = prev.slice();
            next[next.length - 1] = {
              ...next[next.length - 1],
              message: data["overwrite-question"],
            };
            return next;
          });
          return;
        }
        if (data["price-websocket-data"]) {
          const wd = data["price-websocket-data"];
          handlePriceData(
            wd,
            setLatestTickerPrices,
            setDisplayUpdatedInfo,
            websocketActiveChartRef
          );
          return;
        }

        if (data["watchlist-websocket-data"]) {
          handleWebsocketData(data["watchlist-websocket-data"], setWatchlistData, setAnimatedItems)
          return;
        }

        if (data["stream_event"]) {
          const evt = data["stream_event"] || {};
          const eventType = evt["type"];
          const text = typeof evt["text"] === "string" ? evt["text"] : "";
          const fullText =
            typeof evt["fullText"] === "string"
              ? evt["fullText"]
              : typeof evt["full_text"] === "string"
                ? evt["full_text"]
                : null;
          const completed = evt["completed"] === true;
          const streamMessageId =
            evt["streamMessageId"] == null ? null : String(evt["streamMessageId"]);

          if (eventType === "snapshot") {
            resetBufferedStream({
              text: fullText ?? text,
              completed,
              streamMessageId,
            });
            return;
          }

          if (eventType === "delta") {
            enqueueStreamText(text, streamMessageId);
            return;
          }

          if (eventType === "completed") {
            if (typeof fullText === "string") {
              resetBufferedStream({
                text: fullText,
                completed: true,
                streamMessageId,
              });
            }
            streamRenderState.completionPending = true;
            if (streamMessageId) {
              streamRenderState.activeStreamMessageId = streamMessageId;
            }
            finishStreamIfIdle();
            return;
          }
        }

        if (!Object.prototype.hasOwnProperty.call(data, "message")) return;

        enqueueStreamText(data["message"]);
        // if (!useFullWidth) setUseFullWidth(true);
      },
    }
  );

  if (setAsPrimary !== false && websocketSubscriptionRef) {
    websocketSubscriptionRef.current = subscription;
  }

  // Let caller clean up by key
  return {
    key,
    subscription,
    cleanup: () => {
      streamRenderState.destroyed = true;
      clearDrainTimer();
      dropSubscription(key, "thread cleanup");
    },
  };
};

export const connectUserThreadUpdates = ({
  setThreads,
  setMessageThreads,
  activeThreadRef,
  setActiveThread,
  markThreadUnread,
  markThreadRead,
  getLastReadAt,
  getLastReadBotId,
} = {}) => {
  const identifier = {
    channel: "WebsocketMessageChannel",
    scope: "user",
  };
  const key = JSON.stringify(identifier);

  const subscription = ensureSubscription(
    key,
    { url: websocketUrl, identifier },
    {
      connected() {
      },
      disconnected() {
      },
      rejected() {
      },
      received(data) {
        if (!data) return;

        if (typeof window !== "undefined") {
          if (data.user_notification) {
            window.dispatchEvent(
              new CustomEvent("user-notifications:updated", {
                detail: { notification: data.user_notification },
              }),
            );
          }

          if (data.trade_proposal_event) {
            window.dispatchEvent(
              new CustomEvent("trade-proposals:event", {
                detail: data.trade_proposal_event,
              }),
            );
            window.dispatchEvent(new CustomEvent("trade-proposals:updated"));
          }
        }

        if (data.thread_created) {
          const t = data.thread_created;
          if (!t?.threadUuid) return;
          setThreads((prev) => {
            const safePrev = Array.isArray(prev) ? prev : [];
            const next = [
              {
                ...t,
                threadUuid: t.threadUuid,
                title: t.title || "Chat",
                isAdminTargeted: Boolean(t.isAdminTargeted),
                pairedThreadId: t.pairedThreadId ?? t.paired_thread_id,
                pairedUserId: t.pairedUserId ?? t.paired_user_id,
                pairedUserUsername:
                  t.pairedUserUsername ?? t.paired_user_username,
              },
              ...safePrev.filter((row) => row.threadUuid !== t.threadUuid),
            ];
            return next;
          });
          if (activeThreadRef?.current?.threadUuid !== t.threadUuid) {
            if (typeof markThreadUnread === "function") {
              markThreadUnread(t.threadUuid);
            }
          }
          return;
        }

        if (data.thread_message) {
          const payload = data.thread_message;
          const threadUuid = payload?.threadUuid;
          if (!threadUuid) return;

          const msg = payload?.message;
          const isActive = activeThreadRef?.current?.threadUuid === threadUuid;
          setThreads((prev) => {
            const safePrev = Array.isArray(prev) ? prev : [];
            const existing = safePrev.find((row) => row.threadUuid === threadUuid);
            const nextThread = {
              ...(existing || {}),
              threadUuid,
              title: payload?.threadTitle || existing?.title || "Chat",
              updatedAt: payload?.updatedAt || existing?.updatedAt,
              isAdminTargeted: Boolean(payload?.isAdminTargeted ?? existing?.isAdminTargeted),
              pairedUserUsername: existing?.pairedUserUsername,
            };
            return [
              nextThread,
              ...safePrev.filter((row) => row.threadUuid !== threadUuid),
            ];
          });

          if (
            msg &&
            activeThreadRef?.current?.threadUuid === threadUuid &&
            typeof setMessageThreads === "function"
          ) {
            setMessageThreads((prev) => {
              const safePrev = Array.isArray(prev) ? prev : [];
              const already = safePrev.some((m) => m.id === msg.id);
              return already ? safePrev : [...safePrev, msg];
            });
            if (typeof markThreadRead === "function") {
              markThreadRead(threadUuid);
            }
          } else if (!isActive) {
            const isBot = msg?.sender === "bot";
            if (isBot && typeof markThreadUnread === "function") {
              const msgId = Number(msg?.id);
              const lastReadBotId =
                typeof getLastReadBotId === "function"
                  ? Number(getLastReadBotId(threadUuid))
                  : NaN;
              if (
                Number.isFinite(msgId) &&
                Number.isFinite(lastReadBotId) &&
                msgId <= lastReadBotId
              ) {
                return;
              }
              const rawTime = msg?.createdAt || payload?.updatedAt || null;
              const msgTime = rawTime ? Date.parse(rawTime) : null;
              const lastReadAt =
                typeof getLastReadAt === "function"
                  ? getLastReadAt(threadUuid)
                  : null;
              if (lastReadAt && msgTime && msgTime <= lastReadAt) {
                return;
              }
              markThreadUnread(threadUuid);
            }
          }
        }

        if (data.thread_deleted) {
          const threadUuid = data.thread_deleted?.threadUuid;
          if (!threadUuid) return;
          setThreads((prev) => {
            const safePrev = Array.isArray(prev) ? prev : [];
            return safePrev.filter((row) => row.threadUuid !== threadUuid);
          });

          if (activeThreadRef?.current?.threadUuid === threadUuid) {
            if (typeof setActiveThread === "function") setActiveThread(null);
            if (typeof setMessageThreads === "function") setMessageThreads([]);
          }
          if (typeof markThreadRead === "function") {
            markThreadRead(threadUuid);
          }
        }
      },
    },
  );

  return () => dropSubscription(key, "user cleanup");
};

const handlePriceData = (
  websocketData,
  setLatestTickerPrices,
  setDisplayUpdatedInfo,
  websocketActiveChartRef
) => {
  if (!websocketData) return;

  const priceData = websocketData["prices-values"];
  const pctChangeData = websocketData["pct-change"];
  const strPriceData = websocketData["prices"];
  const updatedAtData = websocketData["updated-at"];
  const updatedAtIsoData = websocketData["updated-at-iso"];

  if (!priceData) return;

  const activeChart = websocketActiveChartRef?.current || {};
  const activeIdx = activeChart?.idx == null ? null : String(activeChart.idx);
  const payloadIdx =
    websocketData?.idx == null ? null : String(websocketData.idx);
  const ids = Object.keys(priceData || {});
  if (ids.length === 0) return;
  const activeTickerId =
    activeChart?.tickerId == null ? null : String(activeChart.tickerId);
  const targetIdxs = new Set();

  if (payloadIdx) {
    targetIdxs.add(payloadIdx);
  }

  if (
    activeIdx &&
    activeTickerId &&
    ids.includes(activeTickerId)
  ) {
    targetIdxs.add(activeIdx);
  }

 
  setDisplayUpdatedInfo((prevInfo) => {
    const nextInfo = { ...(prevInfo || {}) };
    ids.forEach((tickerId) => {
      const nextPrice = Number(priceData[tickerId]);
      if (!Number.isFinite(nextPrice)) return;

      const numericId = Number(tickerId);
      if (!Number.isFinite(numericId)) return;

      const formattedPrice = strPriceData?.[tickerId];
      const formattedTime = updatedAtData?.[tickerId];
      const updatedAtIso = updatedAtIsoData?.[tickerId];
      const pct1d = pctChangeData?.[tickerId];
      const currentInfo = nextInfo[numericId] || {};

      console.debug("[chart quote] received", {
        tickerId,
        idx: payloadIdx,
        activeIdx,
        price: nextPrice,
        formattedPrice,
        updatedAtIso,
        formattedTime,
        pct1d,
      });

      nextInfo[numericId] = {
        ...currentInfo,
        currentPrice:
          formattedPrice != null ? formattedPrice : currentInfo.currentPrice,
        currentPriceValue: nextPrice,
        currentPriceUpdatedAt:
          formattedTime != null
            ? formattedTime
            : currentInfo.currentPriceUpdatedAt,
        currentPriceUpdatedAtIso:
          updatedAtIso != null
            ? updatedAtIso
            : currentInfo.currentPriceUpdatedAtIso,
        pricePctChange1d:
          pct1d != null ? pct1d : currentInfo.pricePctChange1d,
      };
    });
    return nextInfo;
  });

  if (targetIdxs.size > 0 && typeof window !== "undefined") {
    try {
      const quotesByTickerId = {};
      ids.forEach((tickerId) => {
        const nextPrice = Number(priceData[tickerId]);
        if (!Number.isFinite(nextPrice)) return;

        quotesByTickerId[String(tickerId)] = {
          currentPrice:
            strPriceData?.[tickerId] != null ? strPriceData[tickerId] : null,
          currentPriceValue: nextPrice,
          currentPriceUpdatedAt:
            updatedAtData?.[tickerId] != null ? updatedAtData[tickerId] : null,
          currentPriceUpdatedAtIso:
            updatedAtIsoData?.[tickerId] != null
              ? updatedAtIsoData[tickerId]
              : null,
          pricePctChange1d:
            pctChangeData?.[tickerId] != null ? pctChangeData[tickerId] : null,
        };
      });

      if (Object.keys(quotesByTickerId).length > 0) {
        targetIdxs.forEach((targetIdx) => {
          window.dispatchEvent(
            new CustomEvent("incite:chat-live-quote", {
              detail: {
                idx: targetIdx,
                quotesByTickerId,
              },
            }),
          );
        });
      }
    } catch (_error) {}
  }

  if (targetIdxs.size === 0) return;

  setLatestTickerPrices((prevPrices) => {
    let nextPrices = prevPrices;

    ids.forEach((tickerId) => {
      const nextPrice = Number(priceData[tickerId]);
      const updatedAtIso = updatedAtIsoData?.[tickerId];
      if (!Number.isFinite(nextPrice) || !updatedAtIso) return;

      targetIdxs.forEach((targetIdx) => {
        nextPrices = mergeQuoteIntoSeriesMap({
          seriesMap: nextPrices,
          tickerId,
          idx: targetIdx,
          price: nextPrice,
          quoteAtIso: updatedAtIso,
        });
      });
    });

    return nextPrices;
  });
};

const handleWebsocketData = (websocketData, setWatchlistData, setAnimatedItems) => {
  if (!websocketData) return

  const priceData     = websocketData["prices"];      // stringified/pretty price
  const pctChangeData = websocketData["pct-change"];  // {}
  if (!priceData) return;

  setWatchlistData(prev => {
    // Build new arrays without mutating prev
    const bump = (oldPrice, newPrice) => (parseFloat(newPrice) > parseFloat(oldPrice) ? "green" : "red");

    const mapBlock = (list) => list.map(item => {
      const p = priceData[item.id];
      if (!p) return item;

      if (String(item.currentPrice) !== String(p)) {
        setAnimatedItems(prevA => ({ ...prevA, [item.id]: bump(item.currentPrice, p) }));
      }
      const addPct = pctChangeData[item.id] === "" ? {} : { pricePctChange1d: pctChangeData[item.id] };
      return { ...item, currentPrice: p, ...addPct };
    });

    const updated = {
      Stocks:      mapBlock(prev.Stocks),
      Cryptos:     mapBlock(prev.Cryptos),
      ForexPairs:  mapBlock(prev.ForexPairs),
      Indices:     mapBlock(prev.Indices),
      Commodities: mapBlock(prev.Commodities),
    };

    // Flash color *before* the numbers flip
    setTimeout(() => setWatchlistData(() => updated), 300);

    // Return previous now; we’ll apply `updated` after delay
    return prev;
  });

}
